import React from 'react';
import { Grid, FormControl, Button, useTheme } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/theme-monokai';
import MentionEditor from '../../TextEditor/MentionEditor';
import SampleParseSection from './SampleParseSection';
import CustomHeader from './CustomHeader';
import useAPIResReqStore from '../useAPIResReqStore';
import WarningBox from '../../pages/CommonStyledComponents/WarningBox';

const HTTP_METHODS = [
  { value: 'POST', label: 'POST' },
  { value: 'GET', label: 'GET' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'PUT', label: 'PUT' },
  { value: 'DELETE', label: 'DELETE' },
];

const ManualSetup = ({
  availablePlaceholders,
  keyPlaceholders,
  valuePlaceholders,
  validateRequest,
  setOnFocus,
}) => {
  const theme = useTheme();
  const aceTheme = theme.palette.mode === 'dark' ? 'monokai' : 'github';
  const handleMethodChange = value => {
    useAPIResReqStore.getState().setRequestType(value);
    useAPIResReqStore.getState().setSampleRequestType(value);
  };

  const handleDataTypeChange = value => {
    useAPIResReqStore.getState().setDataType(value);
    useAPIResReqStore.getState().setSampleDataType(value);
  };

  const handleEndpointUrlChange = value => {
    useAPIResReqStore.getState().setEndPointUrl(value);
    useAPIResReqStore.getState().setSampleEndPointUrl(value);
    validateRequest();
  };

  const handleBodyChange = html => {
    if (html !== useAPIResReqStore.getState().requestData.body) {
      useAPIResReqStore.getState().setApiReqBody(html);
      useAPIResReqStore.getState().setSampleApiReqBody(html);
    }
  };

  const handleAddHeader = () => {
    useAPIResReqStore.getState().addCustomHeaders();
    useAPIResReqStore.getState().addSampleCustomHeaders();
    validateRequest();
  };

  const handleSkipSampleChange = skip => {
    useAPIResReqStore.getState().setSkipSample(skip);
    const rawResponsePlaceholderName = useAPIResReqStore.getState().requestData.rawResponsePlaceholderName;
    if (!skip) {
      useAPIResReqStore.getState().removeKeyInPlaceholder(rawResponsePlaceholderName);
    } else {
      if (rawResponsePlaceholderName) {
        useAPIResReqStore.getState().addKeyInPlaceholder(rawResponsePlaceholderName, 'text');
      }
    }
  };

  const handleRawResponsePlaceholderChange = value => {
    useAPIResReqStore.getState().removeKeyInPlaceholder(useAPIResReqStore.getState().requestData.rawResponsePlaceholderName);
    useAPIResReqStore.getState().setRawResponsePlaceholder(value);
    useAPIResReqStore.getState().addKeyInPlaceholder(
      useAPIResReqStore.getState().requestData.rawResponsePlaceholderName,
      'text'
    );
  };

  return (
    <Grid container className="api-editor-container">
      {/* Warning Box */}
      <Grid item xs={12} sx={{ mb: 3 }}>
        <WarningBox
          text="The manual setup method is deprecated. Please switch to cURL mode for better compatibility and future support."
          width="100%"
          horizontal="flex-start"
        />
      </Grid>

      <Grid item className="width-100">
        <FormControl className="form-control-full">
          <Grid item xs={12} className="setup-mode-label">
            Method
          </Grid>
          <Select
            name="method"
            className="dropdown-mat"
            value={useAPIResReqStore.getState().requestData.request_type}
            defaultValue={'POST'}
            disableUnderline
            variant="standard"
            align="left"
            onChange={e => handleMethodChange(e.target.value)}
          >
            {HTTP_METHODS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item className="width-100">
        <FormControl className="form-control-full">
          <Grid item xs={12} className="setup-mode-label">
            Content Type
          </Grid>
          <Select
            name="dataType"
            className="dropdown-mat"
            defaultValue={'JSON'}
            value={useAPIResReqStore.getState().requestData.data_type}
            disableUnderline
            variant="standard"
            align="left"
            onChange={e => handleDataTypeChange(e.target.value)}
          >
            <MenuItem value={'JSON'}>JSON</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <FormControl className="form-control-full">
        <Grid item xs={12} className="setup-mode-label">
          Endpoint URL
        </Grid>
        <Grid item xs={12} className="width-100">
          <MentionEditor
            availablePlaceholders={availablePlaceholders}
            placeholders={useAPIResReqStore.getState().endpointPlaceholders}
            text={useAPIResReqStore.getState().requestData.endpoint_url}
            placeholder="Insert API endpoint URL here"
            onChange={handleEndpointUrlChange}
            onFocus={() => setOnFocus('endpoint')}
            error={false}
          />
        </Grid>
      </FormControl>
      <FormControl className="form-control-full">
        <Grid item xs={12} className="bodyInput">
          Body
        </Grid>
        <Grid item xs={12} className="width-100">
          <div id="aceditor" className="ace-editor">
            <AceEditor
              mode="javascript"
              theme={aceTheme}
              width="100%"
              height="100%"
              showPrintMargin={false}
              showGutter={true}
              wrapEnabled={true}
              highlightActiveLine={true}
              value={useAPIResReqStore.getState().requestData.body}
              onChange={handleBodyChange}
              fontSize={16}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: false,
                showLineNumbers: true,
                tabSize: 2,
              }}
              editorFocus={() => setOnFocus('editor')}
            />
          </div>
        </Grid>
      </FormControl>
      {useAPIResReqStore.getState().requestData.headers && useAPIResReqStore.getState().requestData.headers.length ? (
        <FormControl className="form-control-full" sx={{ pb: 0 }}>
          <Grid item xs={12} className="custom-header-label">
            Custom Header
          </Grid>
          <CustomHeader
            headers={useAPIResReqStore.getState().requestData.headers}
            headerErrors={false}
            availablePlaceholders={availablePlaceholders}
            onFocus={setOnFocus}
            keyPlaceholders={keyPlaceholders}
            valuePlaceholders={valuePlaceholders}
            validateRequest={validateRequest}
          />
        </FormControl>
      ) : null}
      <Button
        variant="text"
        startIcon={<AddIcon />}
        onClick={handleAddHeader}
        sx={{ mt: 1, mb: 3 }}
      >
        Add Header
      </Button>

      <SampleParseSection
        skipSample={useAPIResReqStore.getState().isSkipSample()}
        onSkipSampleChange={handleSkipSampleChange}
        rawResponsePlaceholderName={useAPIResReqStore.getState().requestData.rawResponsePlaceholderName}
        onRawResponsePlaceholderChange={handleRawResponsePlaceholderChange}
        onFocus={setOnFocus}
        validateRequest={validateRequest}
        isNavigating={true}
      />
    </Grid>
  );
};

export default ManualSetup;
